import { useMemo } from 'react'
import { MdOutlineAssignment } from 'react-icons/md'

import { userManager } from '@/Core/Managers'
import { createCrudResourceContext } from '@/Core/UI/CrudResource/CrudResourceContextProvider'
import { ResourceList } from '@/Core/UI/CrudResource/ResourceList'
import { ListRequestTransformerTask } from '@/Ship/Tasks/ListRequestTransformerTask'

import { TaskResource } from '../Resources/TaskResource'

import type { IPageConfig } from '@/Core/Contracts/Loaders/IPageConfig'

/**
 * «Доручені мені» — я створив АБО я керівник (SPEC-008 §4.4/§4.6):
 * `filters` має нести `or:[createdBy.uuid, manager.uuid]`. Ship-трансформер будує лише
 * AND із плаского prefilter, тому сторінка передає власний `searchParamsTransformer`:
 * викликає базовий `ListRequestTransformerTask`, потім домішує гілку `or`.
 * Локально в контейнері, без правки Ship. `createdBy` не є select-полем ресурсу,
 * тому шлях `createdBy.uuid` задаємо явно.
 */
export default function AssignedTasksPage() {
    const listContext = useMemo(
        () =>
            createCrudResourceContext({
                contextType: 'list',
                registryKey: 'Task.Assigned.list',
                resource: TaskResource,
                list: {
                    searchParamsTransformer: (params: Record<string, unknown>, ctx: unknown) => {
                        const base = ListRequestTransformerTask(params, ctx) as Record<string, unknown>
                        const myUuid = userManager.user?.['uuid']
                        const raw = base['filters']
                        const filters: { and?: unknown[]; or?: unknown[] } =
                            typeof raw === 'string' ? JSON.parse(raw) : {}
                        filters.or = [
                            { field: 'createdBy.uuid', op: '=', value: myUuid },
                            { field: 'manager.uuid', op: '=', value: myUuid },
                        ]
                        base['filters'] = JSON.stringify(filters)
                        return base
                    },
                },
                // Каст на межі нетипізованої .jsx-фабрики: її inferred-параметр не містить
                // resource/prefilter/list (їх фабрика читає через ...props у рантаймі).
                // Приводимо до її ж параметра — без `any` (пор. Core `UniversalResourcePage.tsx:70`).
            } as unknown as Parameters<typeof createCrudResourceContext>[0]),
        []
    )

    // `registryKey` обов'язковий у типі `ResourceList` ЦІЛІ — лишаємо свідомо (§хвіст 05.4).
    return <ResourceList crudContext={listContext} registryKey="Task.Assigned.list" />
}

export const pageConfig: IPageConfig = {
    key: 'Task.Assigned',
    title: 'Доручені мені',
    icon: <MdOutlineAssignment />,
}
